import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import HashPathRedirect from './components/HashPathRedirect';
import { ScrollToTop } from './components/ScrollToTop';
import { useAppInitialization } from './hooks/useAppInitialization';
import { useBundleMetrics } from './hooks/useBundleMetrics';
import { useDeepLinkHandler } from './hooks/useDeepLinkHandler';
import { useStripeReturnDetector } from './hooks/useStripeReturnDetector';
import { useInitialNavigation } from './hooks/useInitialNavigation';
import { useDebugInitialization } from './hooks/useDebugInitialization';
import { trackActivationEvent } from './services/activationAnalyticsService';
import './styles/globals.css';

// Lazy loaded components
const SecureConfigProvider = lazy(() => import('./components/SecureConfigProvider').then(module => ({ default: module.SecureConfigProvider })));
const AuthProviderWrapper = lazy(() => import('./components/AuthProviderWrapper').then(module => ({ default: module.AuthProviderWrapper })));
const ProtectedRoute = lazy(() => import('./components/auth/ProtectedRoute').then(module => ({ default: module.ProtectedRoute })));
const IOSTabLayout = lazy(() => import('./components/navigation/IOSTabLayout').then(module => ({ default: module.IOSTabLayout })));
const AppLayout = lazy(() => import('./components/AppLayout').then(module => ({ default: module.AppLayout })));

// Advanced lazy loading system for optimal bundle splitting
import { 
  LazyCreateRecipePage,
  LazySearchPage,
  LazyMealPlannerPage, 
  LazyShoppingListPage,
  LazyFavoritesPage,
  LazyProfilePage
} from './utils/lazyLoadComponents';

// Direct lazy imports for pages not yet converted
const SearchPage = LazySearchPage;
const FavoritesPage = LazyFavoritesPage;
const ProfilePage = LazyProfilePage;
const CreateRecipePage = LazyCreateRecipePage;
const MealPlannerPage = LazyMealPlannerPage;
const ShoppingListPage = LazyShoppingListPage;

// Import remaining pages directly (to be converted later)
const Settings = lazy(() => import('./pages/Settings'));
const SingleRecipePage = lazy(() => import('./pages/SingleRecipePage'));
const RecipeImportExportPage = lazy(() => import('./pages/RecipeImportExportPage'));
const EditRecipePage = lazy(() => import('./pages/EditRecipePage'));
const RecipeImportOptions = lazy(() => import('./pages/RecipeImportOptions'));
const PasteRecipePage = lazy(() => import('./pages/PasteRecipePage'));
const ImportFromURLPage = lazy(() => import('./pages/ImportFromURLPage'));
const VideosPage = lazy(() => import('./pages/Videos'));


// Landing pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const WedgeLandingPage = lazy(() => import('./pages/WedgeLandingPage'));

// Onboarding flow
const OnboardingFlow = lazy(() => import('./components/Onboarding'));

// Auth pages
const _LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const _RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const _ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const _VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const _TwoFactorPage = lazy(() => import('./pages/auth/TwoFactorPage'));

// Static components that should not be lazy loaded (small and critical)
const DataProvider = lazy(() => import('./components/DataProvider').then(module => ({ default: module.DataProvider })));
// PerformanceMonitor removed from initial load to reduce bundle size

// Removed test components to reduce bundle size - load these only when actually needed
const PublicRecipePage = lazy(() => import('./pages/PublicRecipePage'));
const AccountDeleted = lazy(() => import('./pages/AccountDeleted'));
const Resubscribe = lazy(() => import('./pages/Resubscribe'));
// Legal pages - lazy loaded
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const KnowledgeBaseArticle = lazy(() => import('./components/KnowledgeBaseArticle'));

// Phase 3: Community pages
const CommunityGalleryPage = lazy(() => import('./pages/CommunityGalleryPage'));
const CommunityRecipePage = lazy(() => import('./pages/CommunityRecipePage'));
const CommunityUserProfilePage = lazy(() => import('./pages/CommunityUserProfilePage'));

// Lazy auth routes component
const AuthRoutes = lazy(() => import('./routes/AuthRoutes'));

// Redirect components for community routes
const RedirectToCommunityRecipe: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/app/community/recipe/${slug}`} replace />;
};

const RedirectToCommunityChef: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  return <Navigate to={`/app/community/chef/${username}`} replace />;
};

// Simple recipe list component (not virtualized for initial load)
const SimpleRecipeList = lazy(() => import('./components/RecipeList'));

// Loading component for app sections
const AppSectionSuspense: React.FC = () => (
  <div className="min-h-screen bg-[#E6EFEA] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6651]"></div>
  </div>
);

/**
 * Root Application Component - Phase 1 Foundation + Phase 2 Data Layer
 * 
 * PROJECT_RULES.md Compliance:
 * - ✅ Component size: Under 100 lines (extracted Header and MainLayout)
 * - ✅ Single responsibility: Only handles app structure and routing
 * - ✅ TypeScript: Fully typed React component
 * - ✅ Performance: Lazy loading for bundle splitting
 * - ✅ Security: Configuration managed by SecureConfigProvider
 * 
 * component-architecture.md Compliance:
 * - ✅ Follows hierarchy: App > ErrorBoundary > SecureConfigProvider > PerformanceMonitor > DataProvider > Components
 * - ✅ Phase 2 scope: Added DataProvider for centralized data management
 * - ✅ Clean separation: UI components (Header, MainLayout) extracted for reusability
 * - ✅ Bundle optimization: Lazy loading implemented for all heavy components
 */

// Main route definitions extracted for better organization
const MainRoutes: React.FC = () => {
  const location = useLocation();

  // Handle deep links for native apps
  // console.log('[MainRoutes] Component mounting, initializing handlers');
  useDebugInitialization(); // Debug logging
  useInitialNavigation(); // Check for initial navigation needs
  useDeepLinkHandler();
  useStripeReturnDetector(); // Enhanced Stripe return detection
  useEffect(() => {
    const meaningfulAppPaths = ['/app/recipes', '/app/meal-plan', '/app/shopping-list', '/app/favorites'];

    if (!meaningfulAppPaths.some(path => location.pathname.startsWith(path))) {
      return;
    }

    try {
      const today = new Date().toISOString().slice(0, 10);
      const storageKey = `dinnerbrain_meaningful_app_activity_${today}`;

      if (localStorage.getItem(storageKey) === 'true') {
        return;
      }

      localStorage.setItem(storageKey, 'true');
    } catch {
      // If localStorage is unavailable, still attempt the analytics event once for this route transition.
    }

    void trackActivationEvent('meaningful_app_activity', {
      path: location.pathname,
      source: 'authenticated_app_route',
    });
  }, [location.pathname]);
  
  return (
    <Routes>
      {/* Landing page - default route for non-authenticated users */}
      <Route path="/" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <LandingPage />
        </Suspense>
      } />
      <Route path="/meal-planning-for-tired-brains" element={<Suspense fallback={<AppSectionSuspense />}><WedgeLandingPage /></Suspense>} />
      {/* Onboarding flow */}
      <Route path="/onboarding" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <OnboardingFlow />
        </Suspense>
      } />
      
      {/* Public routes - no authentication required */}
      <Route path="/share/:id" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <PublicRecipePage />
        </Suspense>
      } />
      
      {/* Signup route for Stripe payment redirect */}
      <Route path="/signup" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <SignupPage />
        </Suspense>
      } />
      
      {/* Authentication routes */}
      <Route path="/auth/*" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <AuthRoutes />
        </Suspense>
      } />
      
      {/* Account deleted page - accessible without auth */}
      <Route path="/account-deleted" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <AccountDeleted />
        </Suspense>
      } />
      
      {/* Resubscribe page - for expired subscriptions */}
      <Route path="/resubscribe" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <Resubscribe />
        </Suspense>
      } />

      {/* Legal and contact pages - accessible without auth */}
      <Route path="/privacy" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <PrivacyPolicy />
        </Suspense>
      } />
      <Route path="/terms" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <TermsOfService />
        </Suspense>
      } />
      <Route path="/contact" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <ContactPage />
        </Suspense>
      } />
      <Route path="/blog" element={<Suspense fallback={<AppSectionSuspense />}><BlogIndexPage /></Suspense>} />
      <Route path="/blog/:slug" element={<Suspense fallback={<AppSectionSuspense />}><BlogPostPage /></Suspense>} />
      <Route path="/knowledge-base" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <KnowledgeBasePage />
        </Suspense>
      } />
      <Route path="/knowledge-base/:category/:article" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <KnowledgeBaseArticle />
        </Suspense>
      } />

      {/* Redirect /community to /app/community for compatibility */}
      <Route path="/community" element={<Navigate to="/app/community" replace />} />
      <Route path="/community/recipe/:slug" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <RedirectToCommunityRecipe />
        </Suspense>
      } />
      <Route path="/community/chef/:username" element={
        <Suspense fallback={<AppSectionSuspense />}>
          <RedirectToCommunityChef />
        </Suspense>
      } />

      {/* Protected app routes */}
      <Route path="/app/*" element={
        <ProtectedRoute allowCommunity={true}>
          <AppLayout>
            <IOSTabLayout>
              <Routes>
                <Route index element={<Navigate to="/app/recipes" replace />} />
                <Route path="recipes" element={<Suspense fallback={<AppSectionSuspense />}><SimpleRecipeList /></Suspense>} />
                <Route path="recipes/add" element={<Suspense fallback={<AppSectionSuspense />}><RecipeImportOptions /></Suspense>} />
                <Route path="recipes/new" element={<Suspense fallback={<AppSectionSuspense />}><CreateRecipePage /></Suspense>} />
                <Route path="recipes/paste" element={<Suspense fallback={<AppSectionSuspense />}><PasteRecipePage /></Suspense>} />
                <Route path="recipes/import-url" element={<Suspense fallback={<AppSectionSuspense />}><ImportFromURLPage /></Suspense>} />
                <Route path="recipes/edit/:id" element={<Suspense fallback={<AppSectionSuspense />}><EditRecipePage /></Suspense>} />
                <Route path="recipes/:id" element={<Suspense fallback={<AppSectionSuspense />}><SingleRecipePage /></Suspense>} />
                <Route path="recipes/import-export" element={<Suspense fallback={<AppSectionSuspense />}><RecipeImportExportPage /></Suspense>} />
                <Route path="search" element={<Suspense fallback={<AppSectionSuspense />}><SearchPage /></Suspense>} />
                <Route path="meal-plan" element={<Suspense fallback={<AppSectionSuspense />}><MealPlannerPage /></Suspense>} />
                <Route path="shopping-list" element={<Suspense fallback={<AppSectionSuspense />}><ShoppingListPage /></Suspense>} />
                <Route path="favorites" element={<Suspense fallback={<AppSectionSuspense />}><FavoritesPage /></Suspense>} />
                <Route path="profile" element={<Suspense fallback={<AppSectionSuspense />}><ProfilePage /></Suspense>} />
                <Route path="settings" element={<Suspense fallback={<AppSectionSuspense />}><Settings /></Suspense>} />
                <Route path="videos" element={<Suspense fallback={<AppSectionSuspense />}><VideosPage /></Suspense>} />

                {/* Community routes - now inside AppLayout for iOS compatibility */}
                <Route path="community" element={<Suspense fallback={<AppSectionSuspense />}><CommunityGalleryPage /></Suspense>} />
                <Route path="community/recipe/:slug" element={<Suspense fallback={<AppSectionSuspense />}><CommunityRecipePage /></Suspense>} />
                <Route path="community/chef/:username" element={<Suspense fallback={<AppSectionSuspense />}><CommunityUserProfilePage /></Suspense>} />
              </Routes>
            </IOSTabLayout>
          </AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Redirect any unmatched routes to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export const App: React.FC = () => {
  // console.log('====== APP COMPONENT LOADED ======');
  // console.log('[App] Initial URL:', window.location.href);
  // console.log('[App] Initial Hash:', window.location.hash);
  
  // Initialize app and track metrics
  useAppInitialization();
  useBundleMetrics();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <HashPathRedirect />
        <ScrollToTop />
        <SecureConfigProvider>
          <Suspense fallback={<AppSectionSuspense />}>
              <AuthProviderWrapper>
                <Suspense fallback={<AppSectionSuspense />}>
                  <DataProvider>
                    <MainRoutes />
                  </DataProvider>
                </Suspense>
              </AuthProviderWrapper>
          </Suspense>
        </SecureConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
